'use client';

import { type FC, type MouseEvent, type ReactNode, type SVGProps, useCallback, useMemo } from 'react';
import { clsx } from 'clsx';
import CopyIcon from '../../icons/react/icon-copy';
import { useCopyToClipboardWithTooltip } from '../../util/copy';
import { Button } from '../button/button';
import { Tooltip } from '../tooltip/tooltip';
import styles from './copy-button.module.scss';

export interface CopyButtonTooltip {
  copied?: string;
  copy?: string;
}

export const defaultTooltip: CopyButtonTooltip = {
  copied: 'Copied',
  copy: 'Copy',
};

export interface CopyButtonProps {
  appearance?: 'button' | 'icon' | 'textButton' | ReactNode;
  className?: string;
  content: string;
  icon?: FC<SVGProps<SVGSVGElement>> | ReactNode;
  label: string;
  labelAsChildren?: boolean;
  tooltip?: boolean | CopyButtonTooltip;
}

function getTooltip(tooltip: boolean | CopyButtonTooltip) {
  if (!tooltip) {
    return false;
  }

  if (typeof tooltip === 'boolean') {
    return defaultTooltip;
  }

  return tooltip;
}

export function CopyButton({
  appearance = 'button',
  className,
  content,
  icon: Icon = CopyIcon,
  label = 'Copy',
  labelAsChildren,
  tooltip = true,
}: CopyButtonProps) {
  const tooltipContent = useMemo(() => getTooltip(tooltip), [tooltip]);
  const { showCopiedTooltip, forceTooltipOpen, setShowCopiedTooltip, onCopy, copiedText } = useCopyToClipboardWithTooltip();
  const handleCopy = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      try {
        if (content) {
          e.stopPropagation();
          e.preventDefault();
          await onCopy(content);
        }
      } catch {}
    },
    [content, onCopy],
  );
  const tooltipText = typeof tooltipContent === 'object' ? (copiedText ? tooltipContent.copied : tooltipContent.copy) : undefined;

  let activator: ReactNode = null;

  switch (appearance) {
    case 'icon':
      activator = (
        <button aria-label={label} className={clsx(styles.copyButton, className)} onClick={handleCopy} type="button">
          {typeof Icon === 'function' ? <Icon aria-hidden height={16} /> : Icon}
        </button>
      );

      break;
    case 'button':
      activator = (
        <Button
          affixIcon={typeof Icon === 'function' ? <Icon aria-hidden height={16} /> : Icon}
          aria-label={labelAsChildren ? undefined : label}
          className={clsx(styles.copyButton, className)}
          onClick={handleCopy}
          size="small"
          variant="light"
        >
          {labelAsChildren ? label : null}
        </Button>
      );

      break;
    case 'textButton':
      activator = (
        <Button
          affixIcon={typeof Icon === 'function' ? <Icon aria-hidden height={16} /> : Icon}
          appearance="plain"
          aria-label={labelAsChildren ? undefined : label}
          className={clsx(styles.copyButton, className)}
          onClick={handleCopy}
          size="small"
          variant="light"
        >
          {labelAsChildren ? label : null}
        </Button>
      );

      break;
    default:
      activator = appearance;
  }

  return tooltipText ? (
    <Tooltip activator={activator} onOpenChange={setShowCopiedTooltip} open={showCopiedTooltip || forceTooltipOpen} sideOffset={4}>
      {tooltipText}
    </Tooltip>
  ) : (
    activator
  );
}
